// =============================================================================
// BLOG AUTOMATION SYSTEM
// js/blog-automation.js
// =============================================================================

class BlogSystemAPI {
    constructor() {
        this.baseUrl = window.location.origin;
        this.isLocal = false;
    }

    async triggerScraping(strategies = ["category_deep_dive", "comparison_guide"]) {
        try {
            // If running locally, use mock response
            if (this.isLocal) {
                return this.mockApiResponse("scraping", { strategies });
            }

            const response = await fetch(`${this.baseUrl}/api/scrape`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    action: "scrape_and_generate",
                    strategies: strategies.join(","),
                }),
            });

            return await response.json();
        } catch (error) {
            console.error("Error triggering scraping:", error);
            throw error;
        }
    }

    async generateBlogs(strategies = ["category_deep_dive", "comparison_guide"]) {
        try {
            if (this.isLocal) {
                return this.mockApiResponse("generation", { strategies });
            }

            const response = await fetch(`${this.baseUrl}/api/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    strategies: strategies,
                }),
            });

            return await response.json();
        } catch (error) {
            console.error("Error generating blogs:", error);
            throw error;
        }
    }

    async getWorkflowStatus() {
        try {
            if (this.isLocal) {
                return this.mockStatusResponse();
            }

            const response = await fetch(`${this.baseUrl}/api/status`);
            return await response.json();
        } catch (error) {
            console.error("Error getting status:", error);
            throw error;
        }
    }

    // Mock responses for local development
    mockApiResponse(action, data) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    success: true,
                    message: `✅ Mock ${action} triggered successfully!`,
                    action: action,
                    strategies: data.strategies,
                    timestamp: new Date().toISOString(),
                    note: "This is a mock response for local development",
                });
            }, 1000);
        });
    }

    mockStatusResponse() {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    success: true,
                    latest_runs: [
                        {
                            id: 1,
                            status: "completed",
                            conclusion: "success",
                            created_at: new Date(Date.now() - 300000).toISOString(), // 5 min ago
                            updated_at: new Date(Date.now() - 120000).toISOString(), // 2 min ago
                            head_commit: "Mock: Generated new blog drafts",
                        },
                        {
                            id: 2,
                            status: "in_progress",
                            conclusion: null,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            head_commit: "Mock: Scraping grants data...",
                        },
                    ],
                });
            }, 800);
        });
    }
}

// =============================================================================
// AUTOMATION CONTROL FUNCTIONS
// =============================================================================

const blogAPI = new BlogSystemAPI();

// Main automation functions
async function triggerFullScraping() {
    const button = event.target;
    const strategies = getSelectedStrategies();

    if (strategies.length === 0) {
        showNotification("⚠️ Please select at least one blog strategy", "warning");
        return;
    }

    // Update button state
    setButtonLoading(button, "🚀 Triggering...", true);

    try {
        const result = await blogAPI.triggerScraping(strategies);

        if (result.success) {
            showNotification(`✅ Scraping workflow triggered! ${strategies.length} strategies selected.`, "success");

            // Auto-check status after 5 seconds
            setTimeout(() => {
                checkWorkflowStatus();
            }, 5000);

            // Enable auto-refresh
            if (typeof autoRefresh !== "undefined" && !autoRefresh.isEnabled) {
                autoRefresh.start();
            }
        } else {
            throw new Error(result.error || "Unknown error occurred");
        }
    } catch (error) {
        console.error("Scraping error:", error);
        showNotification(`❌ Error: ${error.message}`, "error");
    } finally {
        setButtonLoading(button, "📊 Scrape & Generate Blogs", false);
    }
}

async function generateBlogsOnly() {
    const button = event.target;
    const strategies = getSelectedStrategies();

    if (strategies.length === 0) {
        showNotification("⚠️ Please select at least one blog strategy", "warning");
        return;
    }

    setButtonLoading(button, "✍️ Generating...", true);

    try {
        const result = await blogAPI.generateBlogs(strategies);

        if (result.success) {
            showNotification(`✅ Blog generation triggered! ${strategies.length} strategies selected.`, "success");

            // Refresh drafts after 10 seconds
            setTimeout(() => {
                if (typeof loadDrafts === "function") {
                    loadDrafts();
                    showNotification("🔄 Refreshed drafts list", "info");
                }
            }, 10000);
        } else {
            throw new Error(result.error || "Unknown error occurred");
        }
    } catch (error) {
        console.error("Generation error:", error);
        showNotification(`❌ Error: ${error.message}`, "error");
    } finally {
        setButtonLoading(button, "✍️ Generate Blogs Only", false);
    }
}

async function checkWorkflowStatus() {
    const button = event?.target;
    const statusDiv = document.getElementById("workflow-status");

    if (button) {
        setButtonLoading(button, "📈 Checking...", true);
    }

    // Update status display
    statusDiv.innerHTML = `
        <div class="status-placeholder">
            <span class="status-icon">⏳</span>
            <span>Checking workflow status...</span>
        </div>
    `;

    try {
        const result = await blogAPI.getWorkflowStatus();
        console.log("📊 Full API Response:", result); // Debug log

        if (result.success) {
            // FIXED: Check for the correct property name
            if (result.latest_blog_runs && result.latest_blog_runs.length > 0) {
                // Use the blog runs data
                displayWorkflowStatus(result.latest_blog_runs);

                // Check if any workflows are running (use new format)
                const hasRunning = result.latest_blog_runs.some(
                    (run) => run.status === "in_progress" || run.status === "queued"
                );

                if (hasRunning) {
                    showNotification("🔄 Workflows are running. Auto-refresh enabled.", "info");
                    if (typeof autoRefresh !== "undefined") {
                        autoRefresh.start();
                    }
                } else if (result.recent_activity) {
                    // Show last activity info
                    const { last_run_conclusion, time_ago } = result.recent_activity;
                    const statusEmoji = last_run_conclusion === "success" ? "✅" : "❌";
                    showNotification(`${statusEmoji} Last workflow: ${last_run_conclusion} (${time_ago})`, "info");
                }
            }
            // FIXED: Handle case where no blog workflows exist
            else if (result.summary) {
                if (result.summary.total_runs === 0) {
                    statusDiv.innerHTML = `
                        <div class="status-placeholder">
                            <span class="status-icon">📝</span>
                            <span>No workflows found yet</span>
                            <p style="font-size: 0.9em; color: #666; margin: 0.5rem 0;">
                                Repository: ${result.repository || "Connected"}<br>
                                Start your first blog generation to see workflow activity here!
                            </p>
                        </div>
                    `;
                } else {
                    statusDiv.innerHTML = `
                        <div class="status-placeholder">
                            <span class="status-icon">🔍</span>
                            <span>No blog workflows found</span>
                            <p style="font-size: 0.9em; color: #666; margin: 0.5rem 0;">
                                Found ${result.summary.total_runs} total workflows, but none are blog-related.<br>
                                Blog workflows will appear here after you trigger generation.
                            </p>
                        </div>
                    `;
                }
            } else {
                // Fallback for unexpected response structure
                statusDiv.innerHTML = `
                    <div class="status-placeholder">
                        <span class="status-icon">❓</span>
                        <span>Unexpected response format</span>
                        <p style="font-size: 0.9em; color: #666; margin: 0.5rem 0;">
                            API connected successfully but returned unexpected data structure.
                        </p>
                    </div>
                `;
                console.warn("Unexpected API response structure:", result);
            }
        } else {
            throw new Error(result.error || "API returned success: false");
        }
    } catch (error) {
        console.error("Status check error:", error);
        statusDiv.innerHTML = `
            <div class="status-placeholder">
                <span class="status-icon">❌</span>
                <span>Could not fetch workflow status</span>
                <p style="font-size: 0.9em; color: #666; margin: 0.5rem 0;">
                    Error: ${error.message}
                </p>
            </div>
        `;
    } finally {
        if (button) {
            setButtonLoading(button, "📈 Check Status", false);
        }
    }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getSelectedStrategies() {
    const checkboxes = document.querySelectorAll('.strategy-checkboxes input[type="checkbox"]:checked');
    return Array.from(checkboxes).map((cb) => cb.value);
}

function setButtonLoading(button, loadingText, isLoading) {
    if (!button) return;

    if (isLoading) {
        button.dataset.originalText = button.textContent;
        button.textContent = loadingText;
        button.disabled = true;
        button.classList.add("btn-automation-loading");
    } else {
        button.textContent = button.dataset.originalText || button.textContent;
        button.disabled = false;
        button.classList.remove("btn-automation-loading");
    }
}

function displayWorkflowStatus(runs) {
    const statusDiv = document.getElementById("workflow-status");

    if (!runs || runs.length === 0) {
        statusDiv.innerHTML = `
            <div class="status-placeholder">
                <span class="status-icon">📝</span>
                <span>No workflow runs found</span>
            </div>
        `;
        return;
    }

    const runsHTML = runs
        .map(
            (run) => `
            <div class="workflow-run">
                <div class="run-status ${run.status}">
                    ${getStatusIcon(run.status, run.conclusion)}
                    <strong>${run.name || run.status}</strong>
                    ${run.conclusion ? `(${run.conclusion})` : ""}
                </div>
                <div class="run-time">${formatTime(run.created_at)}</div>
                <div class="run-commit">
                    ${run.head_commit_message || run.head_commit || "Manual trigger"}
                </div>
                ${run.duration ? `<div class="run-duration">Duration: ${run.duration}</div>` : ""}
                ${
                    run.workflow_url
                        ? `<a href="${run.workflow_url}" target="_blank" class="view-workflow">👁️ View Details</a>`
                        : ""
                }
            </div>
        `
        )
        .join("");

    statusDiv.innerHTML = `
        <div class="workflow-runs">
            <h5 style="margin: 0 0 1rem 0; color: var(--voltade-text-primary); font-size: 1rem;">
                Latest Blog Workflow Runs:
            </h5>
            ${runsHTML}
            <div class="workflow-footer" style="margin-top: 1rem; font-size: 0.85em; color: #666;">
                Auto-refreshes every 30s when workflows are running
            </div>
        </div>
    `;
}
function getStatusIcon(status, conclusion) {
    if (status === "completed") {
        return conclusion === "success" ? "✅" : "❌";
    } else if (status === "in_progress") {
        return "🔄";
    } else if (status === "queued") {
        return "⏳";
    }
    return "❓";
}

function formatTime(timeString) {
    const date = new Date(timeString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) {
        return "Just now";
    } else if (diff < 3600000) {
        return `${Math.floor(diff / 60000)} min ago`;
    } else if (diff < 86400000) {
        return `${Math.floor(diff / 3600000)} hours ago`;
    } else {
        return date.toLocaleDateString();
    }
}

// =============================================================================
// NOTIFICATION SYSTEM
// =============================================================================

function showNotification(message, type = "info", duration = 5000) {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll(".notification");
    existingNotifications.forEach((notification) => notification.remove());

    const notification = document.createElement("div");
    notification.className = `notification notification-${type}`;
    notification.innerHTML = `
        <div class="notification-content">
            <span>${message}</span>
            <button onclick="this.parentElement.parentElement.remove()" class="notification-close">&times;</button>
        </div>
    `;

    document.body.appendChild(notification);

    // Auto-remove after duration
    setTimeout(() => {
        if (notification.parentNode) {
            notification.style.animation = "slideOutNotification 0.3s ease";
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 300);
        }
    }, duration);
}

// =============================================================================
// SETTINGS MENU
// =============================================================================

function showSettingsMenu() {
    // Remove existing menu
    const existingMenu = document.getElementById("settings-menu");
    if (existingMenu) {
        existingMenu.remove();
        return;
    }

    const menuHTML = `
        <div id="settings-menu" class="settings-dropdown">
            <button onclick="toggleAutoRefresh()" class="settings-item">
                ${typeof autoRefresh !== "undefined" && autoRefresh.isEnabled ? "⏸️ Disable" : "🔄 Enable"} Auto-Refresh
            </button>
            <button onclick="refreshDraftsList()" class="settings-item">
                🔄 Refresh Drafts List
            </button>
            <button onclick="selectAllStrategies()" class="settings-item">
                ✅ Select All Strategies
            </button>
            <button onclick="clearAllStrategies()" class="settings-item">
                ❌ Clear All Strategies
            </button>
            <button onclick="viewSystemInfo()" class="settings-item">
                ℹ️ System Information
            </button>
            <button onclick="clearNotifications()" class="settings-item">
                🗑️ Clear Notifications
            </button>
        </div>
    `;

    document.body.insertAdjacentHTML("beforeend", menuHTML);

    // Close menu when clicking outside
    setTimeout(() => {
        document.addEventListener("click", function closeMenu(e) {
            if (!e.target.closest("#settings-menu") && !e.target.closest(".settings-btn")) {
                const menu = document.getElementById("settings-menu");
                if (menu) menu.remove();
                document.removeEventListener("click", closeMenu);
            }
        });
    }, 100);
}

// Settings menu functions
function toggleAutoRefresh() {
    if (typeof autoRefresh !== "undefined") {
        autoRefresh.toggle();
    } else {
        showNotification("Auto-refresh feature not available", "warning");
    }
    document.getElementById("settings-menu")?.remove();
}

function refreshDraftsList() {
    if (typeof loadDrafts === "function") {
        loadDrafts();
        showNotification("🔄 Refreshing drafts list...", "info");
    } else {
        showNotification("Draft loading function not available", "warning");
    }
    document.getElementById("settings-menu")?.remove();
}

function selectAllStrategies() {
    const checkboxes = document.querySelectorAll('.strategy-checkboxes input[type="checkbox"]');
    checkboxes.forEach((cb) => (cb.checked = true));
    showNotification("✅ All strategies selected", "success", 2000);
    document.getElementById("settings-menu")?.remove();
}

function clearAllStrategies() {
    const checkboxes = document.querySelectorAll('.strategy-checkboxes input[type="checkbox"]');
    checkboxes.forEach((cb) => (cb.checked = false));
    showNotification("❌ All strategies cleared", "info", 2000);
    document.getElementById("settings-menu")?.remove();
}

function viewSystemInfo() {
    const strategies = getSelectedStrategies();
    const info = `
🤖 Blog Automation System

📊 Current Configuration:
• Selected Strategies: ${strategies.length > 0 ? strategies.join(", ") : "None"}
• Environment: ${blogAPI.isLocal ? "Local Development" : "Production"}
• Auto-refresh: ${typeof autoRefresh !== "undefined" && autoRefresh.isEnabled ? "Enabled" : "Disabled"}

🎯 Available Strategies:
• category_deep_dive: Category-specific blogs
• agency_focused: Government agency guides
• funding_tier: Strategic funding guides
• business_stage: Business development stage content
• comparison_guide: Grant comparison and selection
• success_stories: Case studies and examples
• troubleshooting: Problem-solving guides

🔧 API Endpoints:
• POST /api/scrape: Trigger scraping workflow
• POST /api/generate: Generate blogs only
• GET /api/status: Check workflow status
    `;

    alert(info);
    document.getElementById("settings-menu")?.remove();
}

function clearNotifications() {
    const notifications = document.querySelectorAll(".notification");
    notifications.forEach((notification) => notification.remove());
    document.getElementById("settings-menu")?.remove();
}

// =============================================================================
// AUTO-REFRESH SYSTEM
// =============================================================================

// Update the AutoRefresh class start method
class AutoRefresh {
    constructor() {
        this.interval = null;
        this.isEnabled = false;
        this.intervalMs = 30000; // 30 seconds
    }

    start(intervalMs = this.intervalMs) {
        if (this.interval) this.stop();

        this.interval = setInterval(async () => {
            try {
                // Check workflow status
                const status = await blogAPI.getWorkflowStatus();

                // FIXED: Use the correct property name
                if (status.success && status.latest_blog_runs) {
                    const hasRunning = status.latest_blog_runs.some(
                        (run) => run.status === "in_progress" || run.status === "queued"
                    );

                    if (hasRunning) {
                        console.log("🔄 Auto-refresh: Workflow running, updating status display...");

                        // Update the workflow status display
                        displayWorkflowStatus(status.latest_blog_runs);

                        // Also refresh drafts if function exists
                        if (typeof loadDrafts === "function") {
                            loadDrafts();
                        }
                    } else {
                        // Stop auto-refresh if no workflows are running
                        this.stop();
                        showNotification("✅ Workflows completed. Auto-refresh disabled.", "success", 3000);
                    }
                } else if (status.success && status.summary?.currently_running === 0) {
                    // No workflows running, stop auto-refresh
                    this.stop();
                    console.log("🔄 Auto-refresh: No workflows running, stopping auto-refresh");
                }
            } catch (error) {
                console.warn("Auto-refresh error:", error);
                // Don't show error notifications during auto-refresh to avoid spam
            }
        }, intervalMs);

        this.isEnabled = true;
        console.log("🔄 Auto-refresh enabled");
    }

    stop() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
        this.isEnabled = false;
        console.log("⏸️ Auto-refresh disabled");
    }

    toggle() {
        if (this.isEnabled) {
            this.stop();
            showNotification("⏸️ Auto-refresh disabled", "info", 2000);
        } else {
            this.start();
            showNotification("🔄 Auto-refresh enabled", "info", 2000);
        }
    }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

// Create global instances
const autoRefresh = new AutoRefresh();

// Make functions available globally
window.triggerFullScraping = triggerFullScraping;
window.generateBlogsOnly = generateBlogsOnly;
window.checkWorkflowStatus = checkWorkflowStatus;
window.showSettingsMenu = showSettingsMenu;
window.autoRefresh = autoRefresh;
window.BlogSystemAPI = BlogSystemAPI;

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
    console.log("🚀 Blog Automation System initialized");

    // Add keyboard shortcuts
    document.addEventListener("keydown", function (e) {
        // Ctrl/Cmd + Shift + S for scraping
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "S") {
            e.preventDefault();
            triggerFullScraping();
        }

        // Ctrl/Cmd + Shift + G for generation
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "G") {
            e.preventDefault();
            generateBlogsOnly();
        }

        // Ctrl/Cmd + Shift + T for status
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "T") {
            e.preventDefault();
            checkWorkflowStatus();
        }
    });

    // Show welcome message
    setTimeout(() => {
        showNotification(
            '🤖 Blog Automation System ready! Select strategies and click "Scrape & Generate"',
            "info",
            4000
        );
    }, 1000);
});

// =============================================================================
// CSS ANIMATIONS (Add to existing CSS)
// =============================================================================

const additionalCSS = `
@keyframes slideOutNotification {
    from {
        transform: translateX(0);
        opacity: 1;
    }
    to {
        transform: translateX(100%);
        opacity: 0;
    }
}
`;

// Inject additional CSS
const style = document.createElement("style");
style.textContent = additionalCSS;
document.head.appendChild(style);

console.log("✅ Blog Automation System loaded successfully!");
